import { useEffect, useRef, useState } from 'react';
import { UseFormReturn, useFieldArray } from 'react-hook-form';

import { AnimatePresence } from 'framer-motion';

import { AddActionButton } from 'components/AddActionButton';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { useToast } from 'hooks/useToaster';
import { getThreeGCommodities } from 'lib/api/getThreeGCommodities';
import { NormalizedLoad, TMSCommodity, createNewCommodity } from 'types/Load';

import { CommodityItemCard } from './components/CommodityItemCard';

export function LoadItemsSection({
  formMethods,
  tmsID,
  customerID,
  customerExternalTmsId,
  enableAutoFreightClass = false,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  tmsID?: number;
  customerID?: number;
  customerExternalTmsId?: string;
  enableAutoFreightClass?: boolean;
}) {
  const {
    control,
    watch,
    clearErrors,
    formState: { errors },
  } = formMethods;
  const errorMessage = errors.commodities?.message;
  const { toast } = useToast();
  const commodityRequestIdRef = useRef(0);
  const [commodities, setCommodities] = useState<TMSCommodity[]>([]);
  const [isLoadingCommodities, setIsLoadingCommodities] = useState(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'commodities',
  });

  const watchedCommodities = watch('commodities');
  const numberOfItems = watchedCommodities?.length || 0;

  useEffect(() => {
    if (!tmsID || (!customerID && !customerExternalTmsId)) {
      commodityRequestIdRef.current += 1;
      setCommodities([]);
      setIsLoadingCommodities(false);
      return;
    }

    const requestId = ++commodityRequestIdRef.current;
    setIsLoadingCommodities(true);

    getThreeGCommodities(tmsID, {
      customerID,
      externalTmsId: customerExternalTmsId,
      forceRefresh: false,
    })
      .then((result) => {
        if (requestId !== commodityRequestIdRef.current) {
          return;
        }

        if (result.isOk()) {
          setCommodities(result.value.commodityList ?? []);
          return;
        }

        setCommodities([]);
        toast({
          description: result.error.message ?? 'Failed to load commodities',
          variant: 'destructive',
        });
      })
      .finally(() => {
        if (requestId === commodityRequestIdRef.current) {
          setIsLoadingCommodities(false);
        }
      });
  }, [customerExternalTmsId, customerID, tmsID, toast]);

  const addCommodity = () => {
    const newCommodity = createNewCommodity({ dimensionUnit: 'inches' });
    newCommodity.weightUnit = 'lbs';
    newCommodity.packagingType = '3'; // Pallet
    append(newCommodity);
    clearErrors('commodities');
  };

  return (
    <div className='w-full'>
      <AddActionButton
        onClick={addCommodity}
        buttonText='Add Item'
        descriptionText='Add item(s) for the shipment'
        buttonNamePosthog='add_commodity_item'
        className='mb-2'
      />

      {errorMessage && (
        <Typography variant='body-xs' className='text-error-500 mb-2'>
          {errorMessage}
        </Typography>
      )}

      <AnimatePresence>
        <Flex
          direction='col'
          gap='md'
          className='w-full mx-0'
          data-testid='threeg-commodities-container'
        >
          {fields.map((field, index) => (
            <CommodityItemCard
              key={field.id}
              index={index}
              formMethods={formMethods}
              onRemove={remove}
              commodities={commodities}
              isLoadingCommodities={isLoadingCommodities}
              tmsID={tmsID}
              customerID={customerID}
              hasCustomer={Boolean(customerID || customerExternalTmsId)}
              hasCustomerId={Boolean(customerID)}
              enableAutoFreightClass={enableAutoFreightClass}
            />
          ))}
        </Flex>
      </AnimatePresence>

      {numberOfItems > 0 && (
        <Typography variant='body-sm' className='mt-2 text-neutral-600'>
          Total items: {numberOfItems}
        </Typography>
      )}
    </div>
  );
}
